import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"

import { totalDataService } from "services/total/total.service"
import { BettingStatsResponse, DepositorStatsResponse, NgrInputs, StatsResponse, TransactionStatsResponse } from "services/total/types"

const DEBOUNCE_MS = 300

function calcNgr(params: NgrInputs): number {
  const { ggr, bonus, cancelledBonusAmount, deposit, withdrawal, positiveGgrFees } = params
  const netBonusCost = bonus - cancelledBonusAmount
  const ggrFee = Math.max(ggr * 0.07, 0)
  return ggr - netBonusCost - ggrFee - positiveGgrFees - deposit * 0.085 - withdrawal * 0.04
}

function assembleStats(
  betting?: BettingStatsResponse,
  transactions?: TransactionStatsResponse,
  depositors?: DepositorStatsResponse
): StatsResponse | undefined {
  if (!betting && !transactions && !depositors) return undefined

  const wager = betting?.wager ?? 0
  const win = betting?.win ?? 0
  // GGR is now computed on the BE (getKPIsBetting) — authoritative casino GGR + MNT sportsbook +
  // balance corrections. Use it directly; fall back to wager − win only if the BE didn't send it.
  const ggr = betting?.ggr ?? wager - win
  const deposit = transactions?.deposit ?? 0
  const withdrawal = transactions?.withdrawal ?? 0
  const bonus = transactions?.bonus ?? 0
  const cancelledBonusAmount = transactions?.cancelledBonusAmount ?? 0
  const positiveGgrFees = betting?.positiveGgrFees ?? 0

  const ngrInputs: NgrInputs = { ggr, bonus, cancelledBonusAmount, deposit, withdrawal, positiveGgrFees }

  return {
    wager,
    win,
    ggr,
    ngr: calcNgr(ngrInputs),
    ngrInputs,
    rakeback: transactions?.rakeback ?? 0,
    reward: transactions?.reward ?? 0,
    vipReward: transactions?.vipReward ?? 0,
    affiliate: transactions?.affiliate ?? 0,
    promo: transactions?.promo ?? 0,
    bonus,
    averageBet: betting?.averageBet ?? 0,
    deposit,
    withdrawal,
    unconfirmedWithdrawal: transactions?.unconfirmedWithdrawal ?? 0,
    netDeposits: deposit - withdrawal,
    depositCount: transactions?.depositCount ?? 0,
    withdrawalCount: transactions?.withdrawalCount ?? 0,
    activeUsers: transactions?.activeUsers ?? 0,
    ftdSum: depositors?.ftdSum ?? 0,
    depositers: depositors?.depositers ?? 0
  }
}

export const useStatsCardGroups = (timeframe: (Date | null)[], tenant: string, betTypes: string[]) => {
  const [bettingLoading, setBettingLoading] = useState(false)
  const [transactionsLoading, setTransactionsLoading] = useState(false)
  const [depositorsLoading, setDepositorsLoading] = useState(false)
  const [betting, setBetting] = useState<BettingStatsResponse>()
  const [transactions, setTransactions] = useState<TransactionStatsResponse>()
  const [depositors, setDepositors] = useState<DepositorStatsResponse>()
  const abortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (timeframe.filter(Boolean).length === 0) return

    if (abortRef.current) abortRef.current.abort()
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      const controller = new AbortController()
      abortRef.current = controller

      setBettingLoading(true)
      setTransactionsLoading(true)
      setDepositorsLoading(true)

      totalDataService
        .getStatsBetting(timeframe, betTypes, controller.signal)
        .then((res) => {
          if (!controller.signal.aborted && res) setBetting(res)
        })
        .catch((err: any) => {
          if (!controller.signal.aborted && err?.status !== 409) toast.error("Failed to load betting stats")
        })
        .finally(() => {
          if (!controller.signal.aborted) setBettingLoading(false)
        })

      totalDataService
        .getStatsTransactions(timeframe, controller.signal)
        .then((res) => {
          if (!controller.signal.aborted && res) setTransactions(res)
        })
        .catch((err: any) => {
          if (!controller.signal.aborted && err?.status !== 409) toast.error("Failed to load transaction stats")
        })
        .finally(() => {
          if (!controller.signal.aborted) setTransactionsLoading(false)
        })

      totalDataService
        .getStatsDepositors(timeframe, controller.signal)
        .then((res) => {
          if (!controller.signal.aborted && res) setDepositors(res)
        })
        .catch((err: any) => {
          if (!controller.signal.aborted && err?.status !== 409) toast.error("Failed to load depositor stats")
        })
        .finally(() => {
          if (!controller.signal.aborted) setDepositorsLoading(false)
        })
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (abortRef.current) abortRef.current.abort()
    }
  }, [timeframe, tenant, betTypes])

  const stats = useMemo(() => assembleStats(betting, transactions, depositors), [betting, transactions, depositors])

  return {
    bettingLoading,
    transactionsLoading,
    depositorsLoading,
    loading: bettingLoading || transactionsLoading || depositorsLoading,
    data: stats
  }
}
